// ============================================================
// PLUMME — AI Advisor: report templates and document assembly
// ============================================================
// Owns which sections each report template is made of, and nothing else. The
// sections themselves live in aiAdvisorReportFinance.jsx and
// aiAdvisorReportOperations.jsx; the models they read
// are derived by backend-advisor-reports.jsx. Splitting it that way means a new
// template is a list of section builders, not a new derivation.
//
// Every template declares the data it claims to be built from (`sourceKeys`).
// That list is rendered in the document's data-basis section, so a template
// cannot quietly claim a source it never reads.
const PlummeAiAdvisorReports = (() => {
  // The section builders are split across two files by subject. Merging them
  // here rather than in a third module keeps the template list as the single
  // place that knows which sections a report is made of.
  const sections = () => {
    const finance = window.PlummeAiAdvisorReportFinance;
    const operations = window.PlummeAiAdvisorReportOperations;
    const analysis = window.PlummeAiAdvisorReportAnalysis;
    const monthly = window.PlummeAiAdvisorReportMonthly;
    return finance && operations && analysis && monthly ? { ...finance, ...operations, ...analysis, ...monthly } : null;
  };
  const safeArray = (value) => (Array.isArray(value) ? value : []);

  // `length` is the page estimate shown on the picker card. It is a range, not
  // a promise: the real page count follows how much the business has booked.
  const REPORT_TEMPLATES = [
    {
      id: 'styre',
      icon: 'briefcase',
      lengthKey: 'adv.rpt.length.variable',
      sourceKeys: ['adv.rpt.source.result', 'adv.rpt.source.balance', 'adv.rpt.source.openItems', 'adv.rpt.source.budget', 'adv.rpt.source.invoices', 'adv.rpt.source.purchases', 'adv.rpt.source.vouchers'],
      compose: (ctx, S) => [
        S.summarySection(ctx, { visual: true }),
        S.changeSinceSection(ctx),
        S.eventsSection(ctx),
        S.keyFiguresSection(ctx),
        S.bridgeSection(ctx),
        S.rollingSection(ctx),
        S.resultTrendSection(ctx),
        S.budgetSection(ctx),
        S.liquiditySection(ctx),
        S.workingCapitalSection(ctx),
        S.balanceSection(ctx),
        S.salesSection(ctx),
        S.costsSection(ctx),
        S.suppliersSection(ctx),
        S.risksSection(ctx),
        S.forecastSection(ctx),
        S.actionsSection(ctx),
        S.decisionsSection(ctx),
      ],
    },
    {
      id: 'manedsrapport',
      icon: 'fileText',
      lengthKey: 'adv.rpt.length.variable',
      sourceKeys: ['adv.rpt.source.result', 'adv.rpt.source.budget'],
      compose: (ctx, S) => [
        S.monthlyStatusSection(ctx),
        S.monthlyBudgetSection(ctx),
        S.monthlyComparisonSection(ctx),
        S.monthlyTrendSection(ctx),
        S.monthlyInsightsSection(ctx),
      ],
    },
    {
      id: 'likviditet',
      icon: 'bank',
      lengthKey: 'adv.rpt.length.variable',
      sourceKeys: ['adv.rpt.source.openItems', 'adv.rpt.source.bank'],
      compose: (ctx, S) => [
        S.summarySection(ctx),
        S.liquiditySection(ctx),
        S.workingCapitalSection(ctx),
        S.forecastSection(ctx),
        S.openItemsSection(ctx, 'sales'),
        S.openItemsSection(ctx, 'purchases'),
      ],
    },
    {
      id: 'avvik',
      icon: 'trendUp',
      lengthKey: 'adv.rpt.length.variable',
      sourceKeys: ['adv.rpt.source.result', 'adv.rpt.source.budget'],
      compose: (ctx, S) => [
        S.summarySection(ctx),
        S.keyFiguresSection(ctx),
        S.bridgeSection(ctx),
        S.budgetSection(ctx),
        S.costsSection(ctx),
      ],
    },
    {
      id: 'kunde',
      icon: 'users',
      lengthKey: 'adv.rpt.length.variable',
      sourceKeys: ['adv.rpt.source.invoices', 'adv.rpt.source.result'],
      compose: (ctx, S) => [
        S.summarySection(ctx),
        S.salesSection(ctx),
        S.customerChangeSection(ctx),
        S.workingCapitalSection(ctx),
        S.openItemsSection(ctx, 'sales'),
      ],
    },
    {
      id: 'kostnad',
      icon: 'receipt',
      lengthKey: 'adv.rpt.length.variable',
      sourceKeys: ['adv.rpt.source.purchases', 'adv.rpt.source.vouchers'],
      compose: (ctx, S) => [
        S.summarySection(ctx),
        S.costsSection(ctx),
        S.suppliersSection(ctx),
        S.voucherReviewSection(ctx),
      ],
    },
    {
      id: 'utestaende',
      icon: 'inbox',
      lengthKey: 'adv.rpt.length.variable',
      sourceKeys: ['adv.rpt.source.openItems'],
      compose: (ctx, S) => [
        S.summarySection(ctx),
        S.openItemsSection(ctx, 'sales'),
        S.openItemsSection(ctx, 'purchases'),
      ],
    },
    {
      id: 'arsoppsummering',
      icon: 'calendar',
      lengthKey: 'adv.rpt.length.variable',
      sourceKeys: ['adv.rpt.source.result', 'adv.rpt.source.balance', 'adv.rpt.source.budget'],
      compose: (ctx, S) => [
        S.summarySection(ctx),
        S.keyFiguresSection(ctx),
        S.bridgeSection(ctx),
        S.rollingSection(ctx),
        S.resultTrendSection(ctx),
        S.balanceSection(ctx),
        S.salesSection(ctx),
        S.costsSection(ctx),
        S.risksSection(ctx),
        S.actionsSection(ctx),
      ],
    },
  ];

  const templateById = (id) => REPORT_TEMPLATES.find((template) => template.id === id) || null;

  // A report for a year that has nothing booked is not an empty report, it is a
  // report that cannot be written. Saying so up front beats eight sections that
  // each repeat "no data".
  const hasAccountingBasis = (ctx) => {
    const report = ctx.report || {};
    return Number(report.inntekter) !== 0
      || Number(report.kostnader) !== 0
      || safeArray(ctx.trial).some((group) => safeArray(group.accounts).length > 0);
  };

  const buildReportDoc = (templateId, ctx = {}) => {
    const template = templateById(templateId);
    const S = sections();
    if (!template || !S) return null;
    const liveBuilders = ['eventsSection', 'liquiditySection', 'workingCapitalSection', 'risksSection', 'forecastSection', 'actionsSection', 'decisionsSection', 'openItemsSection', 'voucherReviewSection'];
    const scoped = ctx.currentContext ? Object.fromEntries(Object.entries(S).map(([name, builder]) => [name,
      liveBuilders.includes(name) ? (_, ...args) => builder(ctx.currentContext, ...args) : builder])) : S;
    const base = {
      templateId: template.id,
      period: ctx.period || null,
      businessName: ctx.settings?.companyName || ctx.settings?.name || ctx.settings?.businessName || '',
      year: ctx.year,
      monthsElapsed: ctx.monthsElapsed,
      generatedAt: ctx.generatedAt,
      status: 'draft',
      previousYearAvailable: Boolean(ctx.previousYearAvailable),
    };
    if (!hasAccountingBasis(ctx)) {
      return {
        ...base,
        hasBasis: false,
        sections: [
          { id: 'sammendrag', titleKey: 'adv.rpt.sec.summary', blocks: [{ type: 'empty', key: 'adv.rpt.empty.noBookkeeping', vars: { year: ctx.year } }] },
          S.dataBasisSection(ctx, template.sourceKeys),
        ],
      };
    }
    return {
      ...base,
      hasBasis: true,
      sections: [
        ...template.compose(ctx, scoped).filter(Boolean).map((section) => {
          const live = ctx.period?.quarter && ['hendelser', 'likviditet', 'arbeidskapital', 'risiko', 'prognose', 'tiltak', 'beslutning', 'kunde', 'leverandor', 'gjennomgang'].includes(section.id);
          const key = live ? 'adv.rpt.period.currentStatus' : ctx.period?.quarter ? section.id === 'rullerende' ? 'adv.rpt.period.rolling' : section.id === 'balanse' ? 'adv.rpt.period.quarterBasis' : null : null;
          return key ? { ...section, blocks: [{ type: 'note', tone: 'info', key, vars: { from: ctx.period.from, to: ctx.period.to, date: String(ctx.generatedAt).slice(0, 10) } }, ...section.blocks] } : section;
        }),
        S.dataBasisSection(ctx, template.sourceKeys),
      ],
    };
  };

  return { REPORT_TEMPLATES, templateById, buildReportDoc, hasAccountingBasis };
})();

Object.assign(window, { PlummeAiAdvisorReports });
